import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSiteMedia } from "../context/SiteMedia";

const BrandMark = () => (
  <svg className="w-8 h-8 text-gold shrink-0" viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" />
    <path
      className="d1-draw"
      pathLength="1"
      d="M11 23V9h6a5 5 0 0 1 0 10h-6"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const LanguageSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useLanguage();
  const base = "px-2.5 py-1 rounded-full transition";
  return (
    <div
      className="flex items-center text-xs font-mono tracking-wider uppercase bg-coal-badge rounded-full p-1 border border-line-strong"
      data-testid="language-switcher"
      role="group"
      aria-label="Language / Taal"
    >
      {["nl", "en"].map((code) => (
        <button
          key={code}
          type="button"
          data-testid={`lang-switch-${code}-btn`}
          onClick={() => setLang(code)}
          aria-pressed={lang === code}
          className={`${base} ${
            lang === code
              ? "bg-gold text-black font-semibold"
              : "text-bone-mute hover:text-white"
          } ${compact ? "px-3 py-1.5" : ""}`}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { t } = useLanguage();
  const { logoUrl } = useSiteMedia();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t.nav.home, id: "nav-link-home" },
    { to: "/about", label: t.nav.about, id: "nav-link-about" },
    { to: "/coaching", label: t.nav.coaching, id: "nav-link-coaching" },
    { to: "/contact", label: t.nav.contact, id: "nav-link-contact" },
  ];

  const navClass = ({ isActive }) =>
    `text-xs font-mono uppercase tracking-widest transition-colors ${
      isActive ? "text-gold" : "text-bone-soft hover:text-white"
    }`;

  return (
    <header
      className="bg-coal-base/90 text-bone border-b border-line-subtle sticky top-0 z-40 backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link" aria-label="Pierre Strength & Technique Coaching — home">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Pierre Strength & Technique Coaching logo"
              className="w-8 h-8 rounded-full object-cover border border-gold/40 shrink-0"
              data-testid="brand-logo-img"
            />
          ) : (
            <BrandMark />
          )}
          <span className="font-display uppercase tracking-wider text-xl text-white">
            {t.brand.name}
          </span>
          <span className="hidden sm:inline-block text-[11px] font-mono tracking-widest text-gold-dim uppercase border-l border-line-strong pl-3">
            {t.brand.tag}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navClass} data-testid={l.id}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 sm:gap-6">
          <LanguageSwitcher />
          <Link
            to="/#intake"
            className="hidden md:inline-flex items-center justify-center px-4 py-2 text-xs font-semibold uppercase tracking-widest bg-gold text-black rounded hover:bg-gold-bright transition-colors"
            data-testid="header-intake-cta"
          >
            {t.nav.intakeCta}
          </Link>
          <button
            type="button"
            className="lg:hidden text-bone-soft hover:text-white p-1"
            onClick={() => setOpen(!open)}
            aria-label={open ? t.nav.menuClose : t.nav.menuOpen}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          className="lg:hidden border-t border-line-subtle bg-coal-base px-6 py-4 flex flex-col gap-4"
          aria-label="Mobile navigation"
          data-testid="mobile-nav"
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className={navClass}
              data-testid={`mobile-${l.id}`}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/#intake"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex items-center justify-center px-4 py-3 text-xs font-semibold uppercase tracking-widest bg-gold text-black rounded hover:bg-gold-bright transition-colors"
            data-testid="mobile-intake-cta"
          >
            {t.nav.intakeCta}
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Header;
